<template>
  <grid-container-fi
    class="teaser-big-image-element-fi"
    :class="{ 'teaser-big-image-element-fi--slider-element': isSliderElement }"
    :container="false"
  >
    <div class="fi-col-span-12 fi-col-start-1">
      <div
        class="teaser-big-image-element-fi__container"
        :class="[
          { 'teaser-big-image-element-fi__container--dark': background === 'dark' },
          { 'teaser-big-image-element-fi__container--light': background === 'light' },
          { 'teaser-big-image-element-fi__container--white': background === 'white' },
          { 'teaser-big-image-element-fi__container--big-image': isBigImage },
          { 'teaser-big-image-element-fi__container--image-pos-right': isImagePosRight },
        ]"
      >
        <div
          class="teaser-big-image-element-fi__column teaser-big-image-element-fi__image-container"
        >
          <div
            v-if="isEditMode && isVideo"
            class="video-player-fi__overlay"
          >
            <div class="video-player-fi__play-button">
              <icon-fi icon="play" />
            </div>
          </div>

          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="!isSliderElement && isEditMode && fields?.image"
            class="teaser-big-image-element-fi__image--editor"
            v-html="fields.image.editable"
          />
          <!-- eslint-enable vue/no-v-html -->

          <div v-if="isSliderElement && isEditMode && fields?.image">
            <img :src="fields.image?.value?.src" />
          </div>

          <div
            v-else-if="!isEditMode && isImageSrc && !isVideo"
            :class="{
              'teaser-big-image-element-fi__image': true,
              'teaser-big-image-element-fi__image--inline-edit': inlineEditMode,
            }"
          >
            <cloud-image-fi
              v-bind="
                fields?.image
                  ? {
                      'field-name': 'image',
                    }
                  : {
                      src: image?.src,
                      alt: image?.alt,
                    }
              "
              :params="imageParams"
              full-height
            />
          </div>

          <div
            v-else-if="!isEditMode && isImageSrc && isVideo && fields"
            class="teaser-big-image-element-fi__video"
          >
            <video-player-fi
              class="teaser-big-image-element-fi__video-player"
              :fields="fields"
              :pause-video="pauseVideo"
              preview-params="func=crop&org_if_sml=0"
            />
          </div>

          <div
            v-if="innovationAwards?.length"
            class="teaser-big-image-element-fi__innovation-awards"
          >
            <div
              v-for="(award, index) in innovationAwards"
              :key="index"
              class="teaser-big-image-element-fi__award"
            >
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="isEditMode && !isSliderElement && award.fields?.image"
                class="teaser-big-image-element-fi__image"
                v-html="award.fields.image.editable"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div
                v-else-if="!isEditMode && award.fields?.image?.value?.src"
                class="teaser-big-image-element-fi__award-img"
              >
                <cloud-image-fi
                  :src="award.fields.image.value.src"
                  :alt="award.fields.image.value.alt"
                  params="func=crop&org_if_sml=0"
                  full-height
                />
              </div>
            </div>
          </div>
        </div>

        <div
          ref="contentContainer"
          class="teaser-big-image-element-fi__column teaser-big-image-element-fi__details-container"
        >
          <div
            ref="contentWrapper"
            class="teaser-big-image-element-fi__details"
          >
            <template v-if="isEditMode && fields?.content">
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="!isSliderElement && fields.content.editable"
                ref="rteContentDiv"
                :class="{
                  'teaser-big-image-element-fi__content richtext-fi': true,
                  'richtext-fi--light': background === 'brand' || background === 'dark',
                }"
                v-html="fields.content.editable"
              />
              <div
                v-else
                ref="rteContentDiv"
                :class="{
                  'teaser-big-image-element-fi__content richtext-fi': true,
                  'richtext-fi--light': background === 'brand' || background === 'dark',
                }"
                v-html="fields.content.value"
              />
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template v-else>
              <!-- eslint-disable vue/no-v-html -->
              <richtext-fi
                ref="rteContentRichtext"
                :class="{
                  'teaser-big-image-element-fi__content': true,
                  'richtext-fi--light': background === 'brand' || background === 'dark',
                }"
                :is-theme-light="background === 'brand' || background === 'dark'"
                field-name="content"
              >
                <slot name="content" />
              </richtext-fi>
              <!-- eslint-enable vue/no-v-html -->
            </template>
          </div>
        </div>
      </div>
    </div>
  </grid-container-fi>
</template>

<script setup lang="ts">
import { IconFi, RichtextFi } from 'atoms';
import { throttle } from 'throttle-debounce';
import { computed, nextTick, onMounted, useTemplateRef, watch } from 'vue';

import CloudImageFi from '@/components/atoms/cloud-image-fi/CloudImageFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import VideoPlayerFi from '@/components/video-player-fi/VideoPlayerFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import { useInlineEdit } from '@/composables/inlineEdit';
import type {
  CheckboxField,
  GeneralLinkField,
  ImageField,
  SingleLineTextField,
} from '@/sitecoreFieldTypes';
import smoothScroll from '@/utils/smoothScroll';

defineOptions({ name: 'TeaserBigImageElementFi' });

interface Fields {
  bigImageSize?: CheckboxField | null;
  content?: SingleLineTextField | null;
  darkBackground?: CheckboxField | null;
  image?: ImageField | null;
  imagePosRight?: CheckboxField | null;
  lightBackground?: CheckboxField | null;
  videoMp4Link?: GeneralLinkField | null;
  videoMp4MobileLink?: GeneralLinkField | null;
  videoMp4TabletLink?: GeneralLinkField | null;
  videoWebmLink?: GeneralLinkField | null;
  videoWebmMobileLink?: GeneralLinkField | null;
  videoWebmTabletLink?: GeneralLinkField | null;
  videoYoutubeLink?: GeneralLinkField | null;
  videoYoutubeMobileLink?: GeneralLinkField | null;
  videoYoutubeTabletLink?: GeneralLinkField | null;
  whiteBackground?: CheckboxField | null;
  teaserBigImageInnovationAwards?: null | Array<{
    fields?: null | {
      image?: ImageField | null;
    };
  }>;
}

const props = withDefaults(
  defineProps<{
    fields?: null | Fields;
    isSliderElement?: boolean;
    pauseVideo?: boolean;
    image?: {
      src?: string | null;
      alt?: string | null;
    } | null;
    imagePosRight?: boolean | null;
    bgColor?: string | null;
  }>(),
  {
    fields: null,
    isSliderElement: false,
    pauseVideo: false,
    image: null,
    imagePosRight: null,
    bgColor: null,
  },
);

const { inlineEditMode } = useInlineEdit('image', props.fields);
const { isMD, windowDimensions } = useBreakpoints();
const fischer = useFischer();

const wrapperClassName = 'teaser-big-image-element-fi__truncate-wrapper';
let throttledResizeListener: ReturnType<typeof throttle> | null = null;

const rteContentDiv = useTemplateRef('rteContentDiv');
const rteContentRichtext = useTemplateRef('rteContentRichtext');
const contentWrapperRef = useTemplateRef('contentWrapper');
const contentContainer = useTemplateRef('contentContainer');

const videoKeys: (keyof Fields)[] = [
  'videoMp4Link',
  'videoWebmLink',
  'videoYoutubeLink',
  'videoMp4TabletLink',
  'videoWebmTabletLink',
  'videoYoutubeTabletLink',
  'videoMp4MobileLink',
  'videoWebmMobileLink',
  'videoYoutubeMobileLink',
];

// TODO: check why this is different from the video composable
const isVideo = computed(() => {
  if (!props.fields) {
    return false;
  }

  for (const key of videoKeys) {
    const link = (props.fields[key] as GeneralLinkField | undefined | null)?.value;
    if (!link) {
      continue;
    }
    const isYoutube = key.includes('Youtube');
    if ((isYoutube && !!link?.url) || (!isYoutube && !!link?.href)) {
      return true;
    }
  }

  return false;
});

const isImageSrc = computed(() => {
  if ((props.image as any)?.src) {
    return true;
  }
  return props.fields?.image?.value?.src;
});

const isBigImage = computed(() => Boolean(props.fields?.bigImageSize?.value));

const isImagePosRight = computed(() => {
  if (props.imagePosRight) {
    return props.imagePosRight;
  }
  return Boolean(props.fields?.imagePosRight?.value);
});

const innovationAwards = computed(() => {
  if (props.fields?.teaserBigImageInnovationAwards?.length) {
    return props.fields.teaserBigImageInnovationAwards.filter(Boolean);
  }
  return [];
});

const background = computed(() => {
  if (props.fields?.darkBackground?.value) {
    return 'dark';
  }
  if (props.fields?.lightBackground?.value) {
    return 'light';
  }
  if (props.fields?.whiteBackground?.value) {
    return 'white';
  }
  if (props.bgColor) {
    return props.bgColor;
  }
  return 'brand';
});

const imageParams = computed(() => {
  if (isMD.value) {
    return 'func=crop&org_if_sml=0';
  }
  return 'aspect_ratio=16:9&org_if_sml=0';
});

const prepareContent = async () => {
  const targetContentElement =
    fischer.app.isEditMode() && !props.isSliderElement
      ? rteContentDiv.value?.querySelector?.('.scWebEditInput')
      : rteContentRichtext.value?.$el?.childNodes[0];
  const contentElements = fischer.app.isEditMode()
    ? targetContentElement?.childNodes
    : rteContentRichtext.value?.$el?.childNodes[0]?.childNodes;
  const firstElement = contentElements?.[0];
  if (!firstElement) {
    return;
  }

  const wrapper = document.createElement('div');
  wrapper.classList.add(wrapperClassName);
  targetContentElement.insertBefore(wrapper, firstElement);

  await nextTick();

  const truncateWrapper = targetContentElement.querySelector(`.${wrapperClassName}`);

  Array.from(contentElements)
    .splice(1)
    .forEach((el: any) => {
      if (el.classList?.contains('button-fi')) {
        return;
      }
      truncateWrapper.appendChild(el);
    });
};

const truncateContent = async () => {
  const targetContentElement =
    fischer.app.isEditMode() && !props.isSliderElement
      ? rteContentDiv.value?.querySelector?.('.scWebEditInput')
      : rteContentRichtext.value?.$el;
  const wrapper = targetContentElement?.querySelector(`.${wrapperClassName}`);
  if (!wrapper) {
    return;
  }
  wrapper.style['-webkit-line-clamp'] = 'unset';
  await nextTick();

  if (!contentWrapperRef.value) {
    console.error('content wrapper div missing');
    return;
  }

  const contentPaddingY =
    Number.parseInt(getComputedStyle(contentWrapperRef.value).paddingTop, 10) +
    Number.parseInt(getComputedStyle(contentWrapperRef.value).paddingBottom, 10);

  let contentHeight =
    (contentWrapperRef.value.childNodes[0] as HTMLElement).offsetHeight + contentPaddingY;
  let lineClamp = 15;
  const updateLineClamp = async () => {
    wrapper.style['-webkit-line-clamp'] = lineClamp;
    await nextTick();
    lineClamp -= 1;

    if (!contentWrapperRef.value) {
      console.error('content div missing in callback');
      return;
    }
    contentHeight =
      (contentWrapperRef.value.childNodes[0] as HTMLElement).offsetHeight + contentPaddingY;

    if (
      contentHeight <= (contentContainer.value?.offsetHeight || Number.MAX_VALUE) ||
      lineClamp === 0
    ) {
      return;
    }

    updateLineClamp();
  };

  if (contentHeight > (contentContainer.value?.offsetHeight || 0)) {
    updateLineClamp();
  }
};

watch(
  windowDimensions,
  () => {
    throttledResizeListener?.();
  },
  { deep: true },
);

onMounted(async () => {
  if (!fischer.app.isEditMode()) {
    await prepareContent();

    if (rteContentRichtext.value) {
      smoothScroll(rteContentRichtext.value.$el);
    }

    if (!IS_SSR) {
      throttledResizeListener = throttle(800, () => truncateContent());
    }

    await nextTick();
    await truncateContent();
  }
});
</script>

<style scoped src="./teaser-big-image-element-fi" />

<style src="./teaser-big-image-element-fi-unscoped" />
